import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { apiErrorCode } from "@/api/client";
import * as endpoints from "@/api/endpoints";
import { Badge, Button, Card, Input, PageTitle } from "@/components/ui/primitives";
import { DataState } from "@/components/data-state";
import { useAuth } from "@/hooks/use-auth";
import { useI18n } from "@/i18n";
import type { Role } from "@/types";

export function MembersPage() {
  const { t } = useI18n();
  const { activeOrgId, role } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = role === "admin";

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>("viewer");
  const [notice, setNotice] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ["members", activeOrgId],
    queryFn: endpoints.listMembers,
  });

  // Only admins may list invitations, so a viewer must not fire the request.
  const invitations = useQuery({
    queryKey: ["invitations", activeOrgId],
    queryFn: endpoints.listInvitations,
    enabled: isAdmin,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["members", activeOrgId] });
    void queryClient.invalidateQueries({ queryKey: ["invitations", activeOrgId] });
  };

  const fail = (error: unknown) => {
    const code = apiErrorCode(error);
    setNotice(
      code === "LAST_ADMIN" ? t("lastAdmin") : code === "FORBIDDEN" ? t("onlyAdmins") : t("error")
    );
  };

  const inviteMutation = useMutation({
    mutationFn: () => endpoints.invite(inviteEmail.trim(), inviteRole),
    onSuccess: () => {
      setInviteEmail("");
      setNotice(t("inviteSent"));
      refresh();
    },
    onError: fail,
  });

  const roleMutation = useMutation({
    mutationFn: (input: { userId: string; role: Role }) =>
      endpoints.setMemberRole(input.userId, input.role),
    onSuccess: () => {
      setNotice(null);
      refresh();
    },
    onError: fail,
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => endpoints.removeMember(userId),
    onSuccess: () => {
      setNotice(null);
      refresh();
    },
    onError: fail,
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => endpoints.revokeInvitation(id),
    onSuccess: refresh,
    onError: fail,
  });

  return (
    <div className="space-y-6">
      <PageTitle>{t("members")}</PageTitle>

      {isAdmin && (
        <Card className="space-y-3 p-4">
          <p className="text-sm font-medium">{t("invite")}</p>
          <div className="flex flex-wrap gap-2">
            <Input
              type="email"
              value={inviteEmail}
              placeholder={t("email")}
              onChange={(event) => setInviteEmail(event.target.value)}
              className="max-w-xs"
            />
            <select
              value={inviteRole}
              onChange={(event) => setInviteRole(event.target.value as Role)}
              aria-label={t("role")}
              className="h-10 rounded-md border border-input bg-card px-3 text-sm"
            >
              <option value="viewer">{t("roleViewer")}</option>
              <option value="admin">{t("roleAdmin")}</option>
            </select>
            <Button
              disabled={!inviteEmail.trim() || inviteMutation.isPending}
              onClick={() => inviteMutation.mutate()}
            >
              {t("invite")}
            </Button>
          </div>
          {notice && <p className="text-sm text-muted-foreground">{notice}</p>}
        </Card>
      )}

      <DataState
        isLoading={members.isLoading}
        isError={members.isError}
        isEmpty={(members.data?.length ?? 0) === 0}
        onRetry={() => void members.refetch()}
      >
        <Card className="divide-y divide-border">
          {members.data?.map((member) => (
            <div key={member.userId} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{member.email ?? member.userId}</p>
                <p className="text-xs text-muted-foreground">
                  {t("joinedAt")}: {new Date(member.joinedAt).toLocaleDateString()}
                </p>
              </div>

              {isAdmin ? (
                <select
                  value={member.role}
                  onChange={(event) =>
                    roleMutation.mutate({
                      userId: member.userId,
                      role: event.target.value as Role,
                    })
                  }
                  aria-label={t("role")}
                  className="h-9 rounded-md border border-input bg-card px-2 text-sm"
                >
                  <option value="viewer">{t("roleViewer")}</option>
                  <option value="admin">{t("roleAdmin")}</option>
                </select>
              ) : (
                <Badge tone={member.role === "admin" ? "primary" : "neutral"}>
                  {member.role === "admin" ? t("roleAdmin") : t("roleViewer")}
                </Badge>
              )}

              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t("remove")}
                  onClick={() => {
                    if (window.confirm(t("confirmRemove"))) {
                      removeMutation.mutate(member.userId);
                    }
                  }}
                >
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              )}
            </div>
          ))}
        </Card>
      </DataState>

      {isAdmin && (invitations.data?.length ?? 0) > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">{t("invitations")}</p>
          <Card className="divide-y divide-border">
            {invitations.data?.map((invitation) => (
              <div key={invitation._id} className="flex items-center gap-3 p-3">
                <span className="min-w-0 flex-1 truncate text-sm">{invitation.email}</span>
                <Badge>{invitation.role === "admin" ? t("roleAdmin") : t("roleViewer")}</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => revokeMutation.mutate(invitation._id)}
                >
                  {t("revoke")}
                </Button>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}
